import { useId, useMemo, useRef, useState } from 'react';
import { useT } from '../i18n/i18n';
import Celebration from './Celebration';
import { STEP, across, makeLot, pathOf, type Car, type Dir } from './carParkRules';

export type CarParkOptions = { size: number; hints: boolean; motion: boolean };

export const CAR_PARK_DEFAULTS: CarParkOptions = { size: 5, hints: false, motion: true };

export function CarParkOptionsForm({ value, onChange }: { value: CarParkOptions; onChange: (v: CarParkOptions) => void }) {
  const t = useT();
  return (
    <>
      <div className="field">
        <label htmlFor="cp-size">{t('Car park size')}</label>
        <select id="cp-size" value={value.size} onChange={(e) => onChange({ ...value, size: Number(e.target.value) })}>
          <option value={5}>{t('Small — 9 cars')}</option>
          <option value={6}>{t('Medium — about 13 cars')}</option>
          <option value={8}>{t('Large — about 20 cars')}</option>
        </select>
      </div>
      <label className="check">
        <input type="checkbox" checked={value.hints} onChange={(e) => onChange({ ...value, hints: e.target.checked })} />
        {t('Show which cars can drive out')}
      </label>
      <label className="check">
        <input type="checkbox" checked={value.motion} onChange={(e) => onChange({ ...value, motion: e.target.checked })} />
        {t('Moving cars: cars drive away')}
      </label>
    </>
  );
}

const PAINT = ['#e76f51', '#2a9d8f', '#e9b949', '#4a78c2', '#8e6bbf', '#5fa55a', '#e07a9b', '#6b7a8f'];

function mix(hex: string, to: number, amount: number) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (to - v) * amount));
  return `rgb(${ch.join(',')})`;
}

const ROTATE: Record<Dir, (len: number) => string> = {
  up: () => '',
  down: (len) => `rotate(180 50 ${len * 50})`,
  right: (len) => `translate(${len * 100} 0) rotate(90)`,
  left: () => 'translate(0 100) rotate(-90)',
};

function CarDrawing({ car, uid }: { car: Car; uid: string }) {
  const h = car.length * 100;
  const long = car.length > 2;
  const fill = `url(#${uid}-p${car.colour})`;
  const glass = '#26303d';
  return (
    <svg viewBox={across(car.dir) ? `0 0 ${h} 100` : `0 0 100 ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <g transform={ROTATE[car.dir](car.length)}>
        <rect x="9" y="5" width="82" height={h - 10} rx="24" fill={fill} />
        <rect x="9" y="5" width="82" height={h - 10} rx="24" fill="none" stroke={mix(PAINT[car.colour], 0, 0.35)} strokeWidth="2.5" />
        <rect x="5" y="44" width="8" height="12" rx="4" fill={mix(PAINT[car.colour], 0, 0.3)} />
        <rect x="87" y="44" width="8" height="12" rx="4" fill={mix(PAINT[car.colour], 0, 0.3)} />
        <path d="M22 40 Q50 30 78 40 L74 58 Q50 53 26 58 Z" fill={glass} />
        <path d="M28 42 Q40 37 52 37 L46 46 Q36 46 30 50 Z" fill="#ffffff" opacity="0.22" />
        {long ? (
          <>
            <rect x="20" y="64" width="60" height={h - 84} rx="8" fill={mix(PAINT[car.colour], 255, 0.32)} />
            <rect x="20" y="64" width="60" height={h - 84} rx="8" fill="none" stroke={mix(PAINT[car.colour], 0, 0.2)} strokeWidth="2" />
            <line x1="30" y1="74" x2="30" y2={h - 30} stroke={mix(PAINT[car.colour], 0, 0.15)} strokeWidth="2" />
            <line x1="70" y1="74" x2="70" y2={h - 30} stroke={mix(PAINT[car.colour], 0, 0.15)} strokeWidth="2" />
          </>
        ) : (
          <>
            <rect x="24" y="60" width="52" height={h - 112} rx="12" fill={mix(PAINT[car.colour], 255, 0.22)} />
            <path d={`M26 ${h - 50} Q50 ${h - 46} 74 ${h - 50} L77 ${h - 36} Q50 ${h - 30} 23 ${h - 36} Z`} fill={glass} />
          </>
        )}
        <rect x="18" y="8" width="16" height="7" rx="3.5" fill="#fff4c2" />
        <rect x="66" y="8" width="16" height="7" rx="3.5" fill="#fff4c2" />
        <rect x="18" y={h - 14} width="14" height="6" rx="3" fill="#d9483b" />
        <rect x="68" y={h - 14} width="14" height="6" rx="3" fill="#d9483b" />
      </g>
    </svg>
  );
}

export default function CarPark({ options, onAgain }: { options: CarParkOptions; onAgain: () => void }) {
  const t = useT();
  const uid = useId().replace(/:/g, '');
  const names: Record<Dir, string> = { up: t('Car facing up'), down: t('Car facing down'), left: t('Car facing left'), right: t('Car facing right') };
  const [lot] = useState(() => makeLot(options.size));
  const [parked, setParked] = useState<Car[]>(lot.cars);
  const [leaving, setLeaving] = useState<Car[]>([]);
  const [moves, setMoves] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const els = useRef(new Map<number, HTMLButtonElement>());
  const done = parked.length === 0;
  const free = useMemo(() => new Set(parked.filter((c) => pathOf(lot, parked, c).blocker === null).map((c) => c.id)), [lot, parked]);

  const shift = (car: Car, cells: number) => {
    const [dr, dc] = STEP[car.dir];
    const pct = (cells / car.length) * 100;
    return across(car.dir) ? `translateX(${dc * pct}%)` : `translateY(${dr * pct}%)`;
  };

  function tap(car: Car) {
    if (done) return;
    const path = pathOf(lot, parked, car);
    const el = els.current.get(car.id);
    setMoves((m) => m + 1);
    if (path.blocker === null) {
      setBlocked(false);
      setParked((p) => p.filter((c) => c.id !== car.id));
      if (!options.motion || !el) return;
      setLeaving((l) => [...l, car]);
      const cells = path.out + car.length + 2;
      const run = el.animate(
        [{ transform: 'none', opacity: 1 }, { transform: shift(car, cells * 0.85), opacity: 1, offset: 0.8 }, { transform: shift(car, cells), opacity: 0 }],
        { duration: 260 + cells * 55, easing: 'cubic-bezier(.5,0,.85,.55)', fill: 'forwards' },
      );
      run.onfinish = () => setLeaving((l) => l.filter((c) => c.id !== car.id));
      return;
    }
    setBlocked(true);
    if (!options.motion || !el) return;
    const reach = path.free + 0.14;
    el.animate(
      [{ transform: 'none' }, { transform: shift(car, reach), offset: 0.55 }, { transform: 'none' }],
      { duration: 280 + path.free * 90, easing: 'ease-in-out' },
    );
    const hit = els.current.get(path.blocker);
    const other = parked.find((c) => c.id === path.blocker);
    if (hit && other) {
      const [dr, dc] = STEP[car.dir];
      hit.animate(
        [{ transform: 'none' }, { transform: `translate(${dc * 3}px, ${dr * 3}px)` }, { transform: 'none' }],
        { duration: 160, delay: (280 + path.free * 90) * 0.5, easing: 'ease-out' },
      );
    }
  }

  const place = (car: Car) => {
    const h = across(car.dir);
    return {
      left: `${(car.col / lot.cols) * 100}%`,
      top: `${(car.row / lot.rows) * 100}%`,
      width: `${((h ? car.length : 1) / lot.cols) * 100}%`,
      height: `${((h ? 1 : car.length) / lot.rows) * 100}%`,
    };
  };

  const shown = [...parked, ...leaving].sort((a, b) => a.id - b.id);

  return (
    <div className="lz-carpark">
      <p className="lz-status" aria-live="polite">
        {done ? t('The car park is clear. You used {n} moves.', { n: moves }) : t('Tap a car to drive it out. Cars left: {n}', { n: parked.length })}
        <span className="lz-score">{t('Moves: {n}', { n: moves })}</span>
      </p>
      <p className="lz-message" role="status">{blocked && !done ? t('That car is blocked. Move the car in front of it first.') : ' '}</p>
      <div className="lz-cp-stage">
        {done && <Celebration text={t('Car park cleared!')} motion={options.motion} />}
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
          <defs>
            {PAINT.map((p, i) => (
              <linearGradient key={i} id={`${uid}-p${i}`} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor={mix(p, 0, 0.28)} />
                <stop offset="0.22" stopColor={p} />
                <stop offset="0.5" stopColor={mix(p, 255, 0.28)} />
                <stop offset="0.78" stopColor={p} />
                <stop offset="1" stopColor={mix(p, 0, 0.28)} />
              </linearGradient>
            ))}
          </defs>
        </svg>
        <div className="lz-cp-road">
          <div className="lz-cp-lot" style={{ aspectRatio: `${lot.cols} / ${lot.rows}`, backgroundSize: `${100 / lot.cols}% ${100 / lot.rows}%` }} role="group" aria-label={t('Car park')}>
            {shown.map((car) => {
              const gone = !parked.includes(car);
              return (
                <button
                  key={car.id}
                  ref={(el) => { if (el) els.current.set(car.id, el); else els.current.delete(car.id); }}
                  type="button"
                  className={`lz-cp-car${options.hints && free.has(car.id) ? ' is-free' : ''}`}
                  style={place(car)}
                  disabled={gone}
                  aria-label={names[car.dir]}
                  onClick={() => tap(car)}
                >
                  <CarDrawing car={car} uid={uid} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="toolbar lz-actions">
        <button type="button" className={done ? 'btn' : 'btn secondary'} onClick={onAgain}>{done ? t('Play again') : t('New car park')}</button>
      </div>
    </div>
  );
}
